import { useEffect } from "react";
import {
  LocaleContext,
  browserLanguages,
  readStoredLocale,
  resolveLocale,
  setActiveLocale,
  writeStoredLocale,
} from "@/i18n";
import { useSession } from "@/features/auth/useSession";

/**
 * Which language the application renders in, decided once for the whole tree.
 *
 * The **only** place the session is read for this purpose, which is what keeps `i18n/` a leaf
 * that knows about languages and nothing about authentication. It does two things:
 *
 * 1. Publishes the resolved locale on `LocaleContext`, which is what `useT()` reads.
 * 2. Publishes it to `i18n/active-locale` for `lib/format.ts`, whose callers are ordinary
 *    functions — a column's `render`, an accessible label, a toast's message — and so cannot
 *    read a context.
 *
 * Step 2 happens **during render**, before any child formats anything. An effect would be a
 * paint too late: the first frame would carry the previous locale's separators.
 *
 * Signed out — the sign-in, invitation and reset screens — there is no preference to honour, so
 * `resolveLocale` falls back to this browser's last known choice and then to its own languages.
 * See its comment for the full order.
 */
export function LocaleProvider({ children }: { children: React.ReactNode }): React.JSX.Element {
  const session = useSession();
  const sessionLocale = session.data?.locale;

  const locale = resolveLocale({
    session: sessionLocale,
    stored: readStoredLocale(),
    languages: browserLanguages(),
  });

  setActiveLocale(locale);

  useEffect(() => {
    // Remembered only once a *user* has told us, so an anonymous visitor's browser language
    // never becomes a sticky preference the next reader inherits.
    if (sessionLocale) writeStoredLocale(sessionLocale);
  }, [sessionLocale]);

  useEffect(() => {
    // Screen readers pick pronunciation from this, and it is the one piece of the document
    // outside React's tree.
    document.documentElement.lang = locale;
  }, [locale]);

  return <LocaleContext.Provider value={locale}>{children}</LocaleContext.Provider>;
}
